// src/oura/merchant/drawers/DatesDrawer.tsx
//
// "Your dates": every date the merchant is waiting for, what happens on it,
// and why it moved when it did. Opened from Coming up on Home and from the
// next-date tile and the "Dates moved" line on Work.
//
// It opens with the term in one line ("Your term"), and it also carries what
// changed at the last schedule publish, in full: the Schedule view's "What
// changed" card, moved here when Schedule became Work.

import { ArrowRightIcon } from "lucide-react";
import type { MerchantSchedule } from "@service/aeo/clientReview";
import { ArrowLink, Drawer, DrawerBlock, DrawerHead, LATE_TEXT, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { DATE_NOTE, slipNote, type ComingDate } from "../model";
import { MERCHANT_V2 } from "../../merchantV2";

export function DatesDrawer({
    open,
    onClose,
    dates,
    term,
    changed,
    onAnswer,
}: {
    open: boolean;
    onClose: () => void;
    dates: ComingDate[];
    /** The term in one line (planSpanText), or null before a plan is sold. */
    term: string | null;
    changed: MerchantSchedule["changed"];
    /** Opens the ask a date waits on. */
    onAnswer: (itemId: string) => void;
}) {
    return (
        <Drawer open={open} onClose={onClose}>
            <DrawerHead title="Your dates" />
            {term && <DrawerBlock label="Your term">{term}</DrawerBlock>}
            {dates.length === 0 && <DrawerBlock>No dates are set yet. They appear here once your plan starts.</DrawerBlock>}
            {dates.map((d, i) => {
                const slip = slipNote(d);
                const note = DATE_NOTE[d.kind];
                const itemId = d.itemId;
                return (
                    <DrawerBlock key={d.key}>
                        <div className="flex items-baseline justify-between gap-3">
                            {/* Numbered in the redesign (16 Oct 2026), so a long term reads as a count. */}
                            <span className="flex min-w-0 items-baseline gap-2.5">
                                {MERCHANT_V2 && (
                                    <span aria-hidden className="grid h-6 w-6 shrink-0 translate-y-[-1px] place-items-center self-center rounded-full bg-blue-50 text-xs font-semibold tabular-nums text-blue-700">
                                        {i + 1}
                                    </span>
                                )}
                                <span className="min-w-0 font-semibold">{d.label}</span>
                            </span>
                            <span className={cn(TYPE.tm, "shrink-0 whitespace-nowrap tabular-nums text-gray-500")}>
                                {dayDate(d.date) ?? "Date to be set"}
                            </span>
                        </div>
                        {note && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>{note}</p>}
                        {slip && <p className={cn(TYPE.tc, slip.late ? LATE_TEXT : "text-gray-600", "m-0 mt-1")}>{slip.text}</p>}
                        {itemId && (
                            <ArrowLink onClick={() => onAnswer(itemId)} className="mt-2">
                                Answer what it waits on
                            </ArrowLink>
                        )}
                    </DrawerBlock>
                );
            })}
            {changed && <WhatChanged changed={changed} />}
        </Drawer>
    );
}

/**
 * What moved since the merchant was last told.
 *
 * The summary sentence leads and the dates follow, because the sentence is
 * what a business owner reads between customers and the list is what they
 * check if they want to. Never a version number on its own.
 */
function WhatChanged({ changed }: { changed: NonNullable<MerchantSchedule["changed"]> }) {
    return (
        <DrawerBlock label="What changed">
            <p className="m-0">{changed.summary}</p>
            <div className="mt-2">
                {changed.moves.map((m, i) => {
                    // The day style the dates above use, so one drawer reads one way.
                    const to = dayDate(m.to) ?? "–";
                    const from = dayDate(m.from);
                    return (
                        <div key={`${m.label}-${i}`} className={cn(TYPE.tc, "flex flex-wrap items-center gap-x-2 border-t border-gray-100 py-2 first:border-t-0")}>
                            <span className="text-gray-500">{m.label}</span>
                            {/* A first publish has no "from": the date is new, not
                                moved, and an arrow from nothing reads as an error. */}
                            {from && (
                                <>
                                    <span className="tabular-nums text-gray-400 line-through">{from}</span>
                                    <ArrowRightIcon aria-hidden size={12} className="text-gray-400" />
                                    <span className="sr-only">moved to</span>
                                </>
                            )}
                            <span className="font-medium tabular-nums text-gray-900">{to}</span>
                        </div>
                    );
                })}
            </div>
        </DrawerBlock>
    );
}
